import React, { useEffect, useMemo, useState } from "react";
import { Clock3, ImagePlus, Loader2, Megaphone, Save, Trash2 } from "lucide-react";

export default function LoginMarketingAdmin({ isSuper }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingName, setDeletingName] = useState("");
  const [errore, setErrore] = useState("");
  const [successo, setSuccesso] = useState("");
  const [headline, setHeadline] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [rotationHours, setRotationHours] = useState(0.25);
  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  async function load() {
    setLoading(true);
    setErrore("");
    try {
      const res = await fetch("/api/admin/login_marketing_get.php", {
        credentials: "include",
      });
      const text = await res.text();
      const data = JSON.parse(text);

      if (!res.ok || !data.ok) {
        throw new Error(data.message || "Errore caricamento contenuti login.");
      }

      setHeadline(data.headline || "");
      setSubheadline(data.subheadline || "");
      setRotationHours(Number(data.rotation_hours) || Number(data.rotation_ms || 5000) / 3600000);
      setImages(Array.isArray(data.images) ? data.images : []);
    } catch (err) {
      setErrore(err.message || "Errore caricamento.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isSuper) return;
    load();
  }, [isSuper]);

  const pendingPreviews = useMemo(
    () =>
      imageFiles.map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
      })),
    [imageFiles]
  );

  useEffect(() => {
    return () => {
      pendingPreviews.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [pendingPreviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrore("");
    setSuccesso("");

    try {
      const fd = new FormData();
      fd.append("headline", headline);
      fd.append("subheadline", subheadline);
      fd.append("rotation_hours", String(rotationHours));
      imageFiles.forEach((file) => fd.append("images[]", file));

      const res = await fetch("/api/admin/login_marketing_save.php", {
        method: "POST",
        credentials: "include",
        body: fd,
      });

      const text = await res.text();
      const data = JSON.parse(text);

      if (!res.ok || !data.ok) {
        throw new Error(data.message || "Errore salvataggio.");
      }

      setImages(Array.isArray(data.images) ? data.images : []);
      setRotationHours(Number(data.rotation_hours) || rotationHours);
      setImageFiles([]);
      setSuccesso("Contenuti login aggiornati con successo.");
    } catch (err) {
      setErrore(err.message || "Errore salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (name) => {
    setDeletingName(name);
    setErrore("");
    setSuccesso("");

    try {
      const res = await fetch("/api/admin/login_marketing_delete_image.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const text = await res.text();
      const data = JSON.parse(text);
      if (!res.ok || !data.ok) {
        throw new Error(data.message || "Errore eliminazione immagine.");
      }

      setImages((prev) => prev.filter((img) => img.name !== name));
      setSuccesso("Immagine rimossa correttamente.");
    } catch (err) {
      setErrore(err.message || "Errore eliminazione.");
    } finally {
      setDeletingName("");
    }
  };

  if (!isSuper) {
    return (
      <div className="p-3 text-sm text-slate-600">
        Solo il superadmin puo modificare il contenuto marketing della login.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 flex items-center gap-2 text-slate-600">
        <Loader2 size={16} className="animate-spin" /> Caricamento contenuti login...
      </div>
    );
  }

  const timelineItems = [...images, ...pendingPreviews];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Megaphone size={18} />
          Login Marketing
        </h2>
        <p className="text-sm text-slate-500">
          Gestisci le immagini della parte sinistra della login e imposta ogni quante ore passare alla successiva.
        </p>
      </div>

      {errore && (
        <div className="bg-amber-100 text-amber-900 px-3 py-2 rounded text-sm">
          {errore}
        </div>
      )}

      {successo && (
        <div className="bg-emerald-100 text-emerald-900 px-3 py-2 rounded text-sm">
          {successo}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1.15fr,0.85fr]">
        <form onSubmit={handleSubmit} className="bg-white border rounded-2xl shadow-sm p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Titolo principale
            </label>
            <textarea
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white transition"
            />
            <p className="mt-1 text-xs text-slate-500">
              Non viene piu mostrato nella parte sinistra, ma resta disponibile in configurazione.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Testo descrittivo
            </label>
            <textarea
              value={subheadline}
              onChange={(e) => setSubheadline(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400 focus:bg-white transition"
            />
            <p className="mt-1 text-xs text-slate-500">
              Anche questo testo non viene mostrato nella nuova area visual.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Durata per immagine
            </label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <Clock3 size={16} className="text-slate-500" />
                <input
                  type="number"
                  min="0.001"
                  max="168"
                  step="0.25"
                  value={rotationHours}
                  onChange={(e) => setRotationHours(Number(e.target.value) || 0.25)}
                  className="w-28 bg-transparent outline-none"
                />
                <span className="text-sm text-slate-500">ore</span>
              </div>
              <span className="text-xs text-slate-500">
                Esempio: `2` = la slide resta visibile per 2 ore
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Aggiungi immagini marketing
            </label>
            <label className="flex items-center justify-center gap-2 w-full rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600 cursor-pointer hover:bg-slate-100 transition">
              <ImagePlus size={18} />
              <span>
                {imageFiles.length
                  ? `${imageFiles.length} immagine/i selezionata/e`
                  : "Seleziona una o piu immagini"}
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
              />
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Formati supportati: JPG, PNG, WEBP, GIF. Max 5 MB per immagine.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? "Salvataggio..." : "Salva contenuti"}
            </button>
          </div>
        </form>

        <div className="space-y-4">
          <div className="bg-slate-900 text-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5">
              <div className="text-xs uppercase tracking-wide opacity-80 mb-2">
                Anteprima login
              </div>
              <p className="text-sm opacity-90">
                La parte sinistra mostra solo immagini a piena copertura, senza testo sovrapposto.
              </p>
              <p className="mt-3 text-xs opacity-80">
                Rotazione attuale: {rotationHours} ore per immagine
              </p>
            </div>
          </div>

          <div className="bg-white border rounded-2xl shadow-sm p-4">
            <div className="text-sm font-semibold text-slate-800 mb-3">
              Timeline immagini
            </div>

            {timelineItems.length ? (
              <div className="space-y-3">
                {timelineItems.map((img, idx) => (
                  <div key={`${img.name}-${idx}`} className="flex gap-3 items-center rounded-xl border border-slate-200 p-3">
                    <div className="w-24 h-16 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                      <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {img.name}
                      </div>
                      <div className="text-xs text-slate-500">
                        Slide {idx + 1}
                      </div>
                    </div>
                    {images.some((item) => item.name === img.name) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(img.name)}
                        disabled={deletingName === img.name}
                        className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                      >
                        {deletingName === img.name ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                        Rimuovi
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                Nessuna immagine caricata.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
